import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Star, MapPin, Clock, Phone, ShoppingBag, Info, ExternalLink, PenTool } from '../../lib/lucide-proxy';
import Button from '../../components/ui/Button';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../config/supabase';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ReviewModal from '../../components/reviews/ReviewModal';
import toast from 'react-hot-toast';

const BreweryPage = () => {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('about');
  const [brewery, setBrewery] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  useEffect(() => {
    fetchBrewery();
  }, [slug]);

  const fetchBrewery = async () => {
    try {
      const { data: breweryData, error: breweryError } = await supabase
        .from('breweries')
        .select('*, reviews(*, user:users(full_name))')
        .eq('slug', slug)
        .single();

      if (breweryError) throw breweryError;

      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('brewery_id', breweryData.id)
        .eq('is_available', true);

      if (productsError) throw productsError;

      // Calc rating
      const avgRating = breweryData.reviews?.length > 0 
        ? breweryData.reviews.reduce((a, b) => a + b.rating, 0) / breweryData.reviews.length 
        : 0;

      setBrewery({
        ...breweryData,
        rating: avgRating,
        reviewCount: breweryData.reviews?.length || 0
      });
      setReviews(breweryData.reviews || []);
      setProducts(productsData || []);

      if (breweryData.is_active && breweryData.delivery_config?.type !== 'none') {
        setActiveTab('menu');
      }
    } catch (error) {
      console.error('Error fetching brewery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addItem({
      ...product,
      brewery_id: brewery.id,
      brewery: { 
        name: brewery.name,
        delivery_config: brewery.delivery_config 
      }
    });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (!brewery) return <div className="min-h-screen flex items-center justify-center">Cervejaria não encontrada</div>;

  return (
    <>
      <Helmet>
        <title>{brewery.name} - Cerveja Delivery</title>
        <meta name="description" content={brewery.description} />
      </Helmet>

      <div className="bg-cream-300 min-h-screen pb-20">
        {/* Hero Cover */}
        <div className="h-[40vh] md:h-[50vh] relative">
          <img 
            src={brewery.cover_image || 'https://images.unsplash.com/photo-1571613316887-6f8d5cbf7ef7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'} 
            alt={brewery.name} 
            className="w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 via-dark-900/40 to-transparent" />
          
          <div className="absolute bottom-0 left-0 w-full p-6 md:p-10">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-end gap-6">
              <img 
                src={brewery.logo || brewery.cover_image} 
                alt="Logo" 
                className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover border-4 border-white/10 shadow-2xl backdrop-blur-sm" 
              />
              <div className="flex-1 text-white mb-2">
                <h1 className="font-display font-bold text-3xl md:text-5xl mb-2 shadow-black drop-shadow-lg">
                  {brewery.name}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm md:text-base opacity-90">
                  <div className="flex items-center bg-white/20 px-3 py-1 rounded-full backdrop-blur-md">
                    <MapPin className="w-4 h-4 mr-1" /> {brewery.address}
                  </div>
                  <div className="flex items-center text-yellow-400">
                    <Star className="w-4 h-4 fill-current mr-1" />
                    <span className="font-bold">{brewery.rating.toFixed(1)}</span>
                    <span className="text-white/70 ml-1">({brewery.reviewCount} avaliações)</span>
                  </div>
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setIsReviewModalOpen(true)} className="mb-2">
                <Star className="w-4 h-4 mr-2" /> Avaliar
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Navigation Tabs */}
          <div className="flex space-x-1 bg-white/50 p-1 rounded-xl backdrop-blur-sm w-fit mb-8">
            <button
              onClick={() => setActiveTab('about')}
              className={`flex items-center px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'about' 
                  ? 'bg-white text-primary-600 shadow-sm' 
                  : 'text-dark-600 hover:text-primary-600 hover:bg-white/50'
              }`}
            >
              <Info className="w-4 h-4 mr-2" /> Sobre
            </button>
            {brewery.is_active && (
              <button
                onClick={() => setActiveTab('menu')}
                className={`flex items-center px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === 'menu' 
                    ? 'bg-white text-primary-600 shadow-sm' 
                    : 'text-dark-600 hover:text-primary-600 hover:bg-white/50'
                }`}
              >
                <ShoppingBag className="w-4 h-4 mr-2" /> Menu
              </button>
            )}
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'about' && (
              <motion.div
                key="about"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-3 gap-8"
              >
                <div className="lg:col-span-2 space-y-8">
                  <div className="bg-white rounded-2xl p-8 shadow-soft">
                    <h2 className="font-display font-bold text-2xl text-dark-900 mb-4">Nossa História</h2>
                    <p className="text-dark-600 leading-relaxed text-lg">
                      {brewery.description || 'Sem descrição disponível.'}
                    </p>
                  </div>

                  {/* Reviews Section */}
                  <div className="bg-white rounded-2xl p-8 shadow-soft">
                    <h2 className="font-display font-bold text-2xl text-dark-900 mb-6">Avaliações dos Confrades</h2>
                    {reviews.length === 0 ? (
                      <p className="text-dark-500">Seja o primeiro a avaliar!</p>
                    ) : (
                      <div className="space-y-6">
                        {reviews.map((review) => (
                          <div key={review.id} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                            <div className="flex justify-between mb-2">
                              <span className="font-semibold text-dark-900">{review.user?.full_name || 'Usuário'}</span>
                              <div className="flex text-yellow-400">
                                {[...Array(review.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                              </div>
                            </div>
                            <p className="text-dark-600 text-sm">{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-dark-900 text-white rounded-2xl p-8 shadow-lg">
                    <h3 className="font-display font-bold text-xl mb-4">Visite-nos</h3>
                    <p className="text-gray-300 mb-6">
                      {brewery.address}
                    </p>
                    <Button variant="primary" className="w-full" onClick={() => window.open(`https://maps.google.com/?q=${brewery.address}`, '_blank')}>
                      Ver no Mapa
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'menu' && (
              <motion.div
                key="menu"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {/* ... (Product Grid remains the same) ... */}
                <div className="flex justify-between items-end mb-6">
                  <div>
                    <h2 className="font-display font-bold text-2xl text-dark-900">Menu Digital</h2>
                    <p className="text-dark-500">
                      {brewery.delivery_config?.type === 'none' ? 'Confira nossos produtos disponíveis.' : 'Peça online e receba em casa.'}
                    </p>
                  </div>
                  {brewery.delivery_config?.type === 'external' && (
                    <Button variant="primary" onClick={() => window.open(brewery.delivery_config.link, '_blank')} className="flex items-center">
                      <ExternalLink className="w-4 h-4 mr-2" /> Pedir no App
                    </Button>
                  )}
                </div>

                {products.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl">
                    <p className="text-dark-500">Nenhum produto disponível no momento.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map(product => (
                      <div key={product.id} className="bg-white rounded-2xl shadow-soft border border-gray-100 overflow-hidden hover:shadow-medium transition-all group">
                        <div className="h-48 relative overflow-hidden">
                          <img 
                            src={product.image || 'https://images.unsplash.com/photo-1518176258769-f227c798150e?auto=format&fit=crop&w=500&q=80'} 
                            alt={product.name} 
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" 
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                            <div className="flex gap-3 text-white text-xs font-bold tracking-wider">
                              {product.abv && <span className="bg-white/20 backdrop-blur-md px-2 py-1 rounded">ABV: {product.abv}%</span>}
                              {product.ibu && <span className="bg-white/20 backdrop-blur-md px-2 py-1 rounded">IBU: {product.ibu}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="p-5">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-bold text-lg text-dark-900 leading-tight">{product.name}</h3>
                            <span className="font-bold text-primary-600 text-lg">R$ {product.price.toFixed(2)}</span>
                          </div>
                          <p className="text-sm text-dark-600 mb-5 line-clamp-2 min-h-[2.5em]">
                            {product.description}
                          </p>
                          
                          {brewery.delivery_config?.type !== 'none' && brewery.delivery_config?.type !== 'external' ? (
                            <Button variant="primary" className="w-full shadow-none hover:shadow-md" onClick={() => handleAddToCart(product)}>
                              Adicionar ao Pedido
                            </Button>
                          ) : brewery.delivery_config?.type === 'external' ? (
                            <Button variant="outline" className="w-full" onClick={() => window.open(brewery.delivery_config.link, '_blank')}>
                              Ver no App de Entrega
                            </Button>
                          ) : (
                            <Button variant="secondary" className="w-full" disabled>
                              Apenas Consumo Local
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      
      <ReviewModal 
        isOpen={isReviewModalOpen} 
        onClose={() => setIsReviewModalOpen(false)} 
        breweryId={brewery.id} 
        onSuccess={fetchBrewery}
      />
    </>
  );
};

export default BreweryPage;